import './style/style.scss';

import { faBars, faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AppBar, Box, ListItemText, Menu, MenuItem, Toolbar, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { isNavLabelEnabled, restrictNavByPermission } from '../../config/feature-flags';
import ImersiveLogoDarkMobile from '../../assets/immersive-logo-dark-mobile.svg';
import ImersiveLogoDark from '../../assets/immersive-logo-dark.svg';
import { ProfileOption, usePreferences } from '../../hooks/use-preferences';
import AuthContext from '../../providers/auth/auth-service';
import { GlobalContext } from '../../providers/global-provider';
import { riskTypes } from '../../providers/risk-management/risk-management-provider';
import UserMenu from '../userMenu/user-menu';

import { selectAllowedLinks } from './allowed-links';
import DrawerMenu from './drawer-menu';
import { buildNavEntries, NavEntry, NavGroup, selectVisibleNavLinks } from './nav-entries';

export type AppBarlink = {
  label: string;
  url: string;
  permissions?: string;
  allowed?: boolean;
};

const LINKS: AppBarlink[] = [
  { label: 'Market Data', url: '/dashboard' },
  {
    label: 'Market Probabilities',
    url: '/dashboard/market-probabilities',
    permissions: 'market_data',
  },
  {
    label: 'Options Flow',
    url: '/dashboard/options-flow',
    permissions: 'market_data',
  },
  {
    label: 'Carry & Basis',
    url: '/dashboard/carry-basis',
    permissions: 'market_data',
  },
  { label: 'Portfolio Management', url: '/portfolio-management' },
  { label: 'Portfolio Performance', url: '/portfolio-performance', permissions: 'risk' },
  { label: 'OTC Trader', url: '/otc-trader', permissions: 'otc-trader' },
];

// Purely presentational grouping for the top nav — the underlying LINKS
// (routes/permissions) are unchanged; this just nests several of them under one
// dropdown-triggering parent. Children render in childLabels order; any label
// with no matching LINKS entry is simply skipped, so the curated 7 items are
// the only source of truth.
const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Markets',
    childLabels: ['Market Data', 'Market Probabilities', 'Carry & Basis', 'Options Flow'],
  },
];

export default function ImmersiveAppBar() {
  const { setCurrentTab, currentTab, renderSettings, setRenderSettings, setParams } =
    useContext(GlobalContext);
  const { permissions } = useContext(AuthContext);
  const { preferences } = usePreferences();
  const navigate = useNavigate();

  const isMobileOrTablet = useMediaQuery('(max-width: 1024px)');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Separate anchor/state for the nav-group dropdown (Markets), kept independent
  // of the Risk Management menu above.
  const [groupAnchorEl, setGroupAnchorEl] = useState<null | HTMLElement>(null);
  const [openGroupLabel, setOpenGroupLabel] = useState<string | null>(null);

  const [openDrawer, setOpenDrawer] = useState(false);

  const ImmersiveLogo = isMobileOrTablet ? ImersiveLogoDarkMobile : ImersiveLogoDark;

  // Active tab tracked by label (not a numeric Tabs index): a group breaks the
  // contiguous-index model, so highlighting keys off the label instead.
  const [activeLabel, setActiveLabel] = useState<string | null>(null);

  // OFF (production default): identical to `LINKS`. ON (restricted env): drops
  // links the user has no permission for. See IM-2594.
  const allowedLinks = selectAllowedLinks(LINKS, permissions, restrictNavByPermission);

  // IM-2595: OFF → same reference as allowedLinks (feature-off entries kept and
  // greyed by the render layer, as today). ON → feature-off entries removed, so
  // the nav shows only links that are BOTH permitted AND feature-enabled.
  const visibleLinks = selectVisibleNavLinks(
    allowedLinks,
    restrictNavByPermission,
    isNavLabelEnabled,
  );

  const isFullUserProfile = preferences.profile === ProfileOption.Full;

  const navEntries = useMemo<NavEntry<AppBarlink>[]>(
    () => buildNavEntries(visibleLinks, NAV_GROUPS),
    [visibleLinks],
  );

  // Preserves beta's original tab-click semantics: while on /dashboard, switch
  // the active tab via currentTab; from any other route, bounce back to
  // /dashboard and reset to the first link (unchanged from the flat Tabs
  // onChange handler).
  const navigateToTab = (link: AppBarlink) => {
    setRenderSettings(false);

    if (window.location.pathname !== '/dashboard') {
      navigate('/dashboard');
      // visibleLinks can be empty in restricted mode (every surviving link
      // feature-off); only seed the active tab when there is one.
      const firstLabel = visibleLinks[0]?.label;
      if (firstLabel) {
        setActiveLabel(firstLabel);
        setCurrentTab(firstLabel);
      }
      return;
    }

    setActiveLabel(link.label);
    setCurrentTab(link.label);
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const closeGroupMenu = () => {
    setGroupAnchorEl(null);
    setOpenGroupLabel(null);
  };

  const openGroupMenu = (anchor: HTMLElement, label: string) => {
    setGroupAnchorEl(anchor);
    setOpenGroupLabel(label);
  };

  // Enter/Space activate a focused nav trigger, matching what MUI Tab gave for
  // free before the Tabs -> Box swap.
  const isActivationKey = (event: React.KeyboardEvent) =>
    event.key === 'Enter' || event.key === ' ';

  const renderRiskManagementOptions = () => {
    const onOptionClick = (type: riskTypes) => {
      onClose();
      setCurrentTab(riskTypes.spotLadder === type ? 'Risk Management' : 'Risk Management Spot');
      setParams('Risk Management', {
        type: type,
      });
    };

    return (
      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
      >
        <MenuItem onClick={() => onOptionClick(riskTypes.spotLadder)}>
          <ListItemText disableTypography>Spot Ladder</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onOptionClick(riskTypes.spotGrid)}>
          <ListItemText disableTypography>Spot Grid</ListItemText>
        </MenuItem>
      </Menu>
    );
  };

  const openGroupEntry = navEntries.find(
    (entry): entry is Extract<NavEntry<AppBarlink>, { type: 'group' }> =>
      entry.type === 'group' && entry.label === openGroupLabel,
  );

  const renderGroupMenu = () => (
    <Menu
      className="nav-group-menu"
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      anchorEl={groupAnchorEl}
      open={Boolean(groupAnchorEl)}
      onClose={closeGroupMenu}
      PaperProps={{ className: 'nav-group-menu-paper' }}
      MenuListProps={{ className: 'nav-group-menu-list' }}
    >
      {openGroupEntry?.links.map((link) => {
        // Feature-gated child: render greyed + non-clickable when its flag is
        // OFF (MUI `disabled` blocks the click and greys the row).
        // OFF-mode greying only; in restricted (ON) mode feature-off links are
        // removed upstream so this never fires. Keep it - production (OFF) greys.
        const enabled = isNavLabelEnabled(link.label);
        return (
          <MenuItem
            key={link.label}
            className="nav-group-menu-item"
            selected={link.label === activeLabel}
            disabled={!enabled}
            onClick={() => {
              if (!enabled) return;
              navigateToTab(link);
              closeGroupMenu();
            }}
          >
            <ListItemText disableTypography>{link.label}</ListItemText>
          </MenuItem>
        );
      })}
    </Menu>
  );

  const renderLinks = useMemo(() => {
    return navEntries.map((entry) => {
      if (entry.type === 'link') {
        const isActive = entry.link.label === activeLabel;
        // Feature-gated top-level link (e.g. Portfolio Performance): visible but
        // greyed + non-clickable when its flag is OFF.
        // OFF-mode greying only; restricted (ON) mode removes feature-off links
        // upstream so this never fires. Keep it - production (OFF) greys.
        const enabled = isNavLabelEnabled(entry.link.label);
        return (
          <Box
            key={entry.link.label}
            role="button"
            tabIndex={enabled ? 0 : -1}
            aria-current={isActive ? 'page' : undefined}
            aria-disabled={enabled ? undefined : true}
            className={`MuiTab-root link${isActive ? ' Mui-selected' : ''}${
              enabled ? '' : ' nav-disabled'
            }`}
            onClick={() => {
              if (!enabled) return;
              navigateToTab(entry.link);
            }}
            onKeyDown={(event) => {
              if (!enabled || !isActivationKey(event)) return;
              event.preventDefault();
              navigateToTab(entry.link);
            }}
          >
            {entry.link.label}
          </Box>
        );
      }

      const isActive = entry.links.some((l) => l.label === activeLabel);
      return (
        <Box
          key={entry.label}
          role="button"
          tabIndex={0}
          aria-haspopup="menu"
          aria-expanded={openGroupLabel === entry.label}
          aria-current={isActive ? 'page' : undefined}
          className={`MuiTab-root link nav-group${isActive ? ' Mui-selected' : ''}`}
          onClick={(event) => {
            setRenderSettings(false);
            openGroupMenu(event.currentTarget, entry.label);
          }}
          onKeyDown={(event) => {
            if (!isActivationKey(event)) return;
            event.preventDefault();
            setRenderSettings(false);
            openGroupMenu(event.currentTarget, entry.label);
          }}
        >
          {/* Underline is scoped to this label span (not the whole button) so
              the active-tab underline stops at the text and doesn't run under
              the chevron. */}
          <span className="nav-group-label">{entry.label}</span>
          <FontAwesomeIcon icon={faChevronDown} className="nav-group-chevron" aria-hidden />
        </Box>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigateToTab/openGroupMenu/setRenderSettings are stable per render, not worth threading through deps
  }, [navEntries, activeLabel, openGroupLabel]);

  useEffect(() => {
    const pathname = window.location.pathname;

    if (pathname === '/terms-of-use' || pathname === '/privacy-policy' || renderSettings) {
      setActiveLabel(null);
    } else {
      const active = visibleLinks.find((link) => currentTab.startsWith(link.label));

      if (active) {
        if (pathname !== '/dashboard') {
          setActiveLabel(null);
          if (visibleLinks[0]) setCurrentTab(visibleLinks[0].label);
        } else {
          setActiveLabel(active.label);
        }
      } else if (visibleLinks[0]) {
        // The current tab is not among the visible links - e.g. a restricted env
        // whose default tab (Market Data) was filtered out of the nav. Land on
        // the first visible tab rather than leaving an inaccessible default on
        // screen. No-op for normal envs: their default tab is visible, so `active`
        // is found above. Converges in one pass (next render currentTab is visible).
        setActiveLabel(visibleLinks[0].label);
        setCurrentTab(visibleLinks[0].label);
      }
    }
  }, [visibleLinks, currentTab, renderSettings]);

  const toggleDrawer = () => {
    setOpenDrawer((prev) => !prev);
  };

  const onClickDrawer = (label: string) => {
    setRenderSettings(false);
    const link = visibleLinks.find((l) => l.label === label);
    if (link) {
      setActiveLabel(label);
      setCurrentTab(label);
    }
  };

  const renderTabs = () => {
    const mobileRender = <Box sx={{ flexGrow: 1, display: 'flex' }}></Box>;

    const normalRender = (
      <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
        <Box className="tabs" role="navigation" aria-label="Primary">
          {renderLinks}
        </Box>
        {renderRiskManagementOptions()}
        {renderGroupMenu()}
      </Box>
    );

    return isMobileOrTablet ? mobileRender : normalRender;
  };

  return (
    <AppBar className="immersive-appbar">
      <Toolbar disableGutters className="immersive-appbar-content">
        <Box sx={{ mr: '56px', display: 'flex', alignItems: 'center' }}>
          <RouterLink to="/dashboard" style={{ display: 'flex', alignItems: 'center' }}>
            <img src={ImmersiveLogo} alt="Immersive finance" className="immersive-logo" />
          </RouterLink>
          <Box
            sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 2 }}
            onClick={toggleDrawer}
            className="menu-button"
          >
            <FontAwesomeIcon icon={faBars} className="icon" />
            <span>Menu</span>
          </Box>
        </Box>
        {renderTabs()}
        <Box sx={{ display: 'flex' }}>
          <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }} sx={{ alignItems: 'center' }}>
            <Grid>
              <UserMenu hasPermission={true} isFullUserProfile={isFullUserProfile} />
            </Grid>
          </Grid>
        </Box>
        {!!isMobileOrTablet && (
          <DrawerMenu
            open={openDrawer}
            onClose={toggleDrawer}
            links={visibleLinks}
            onClick={onClickDrawer}
          />
        )}
      </Toolbar>
    </AppBar>
  );
}
